import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// workspace.page_favorites(user_id, page_id, created_at) 훅.
// 내 즐겨찾기 page_id 목록 조회 + toggle.
// 테이블이 아직 없을 때(마이그레이션 전)도 깨지지 않도록 try/catch 로 방어 — 빈 목록·no-op.
export default function useFavorites() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [favoriteIds, setFavoriteIds] = useState([]); // page_id 배열
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    if (!userId) {
      setFavoriteIds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .schema('workspace')
        .from('page_favorites')
        .select('page_id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) {
        // 테이블 미존재(42P01) 등 — 조용히 빈 목록 처리.
        setFavoriteIds([]);
      } else {
        setFavoriteIds((data || []).map((r) => r.page_id));
      }
    } catch {
      setFavoriteIds([]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // 다른 곳에서 즐겨찾기를 바꾸면 갱신
  useEffect(() => {
    const h = () => fetchFavorites();
    window.addEventListener('rt:favorites', h);
    return () => window.removeEventListener('rt:favorites', h);
  }, [fetchFavorites]);

  const isFavorite = useCallback(
    (pageId) => favoriteIds.includes(pageId),
    [favoriteIds]
  );

  // 즐겨찾기 추가/해제. 테이블 없으면 no-op.
  const toggleFavorite = useCallback(
    async (pageId) => {
      if (!userId || !pageId) return false;
      const already = favoriteIds.includes(pageId);
      // 낙관적 갱신
      setFavoriteIds((prev) =>
        already ? prev.filter((id) => id !== pageId) : [pageId, ...prev]
      );
      try {
        if (already) {
          const { error } = await supabase
            .schema('workspace')
            .from('page_favorites')
            .delete()
            .eq('user_id', userId)
            .eq('page_id', pageId);
          if (error) { await fetchFavorites(); return false; }
        } else {
          const { error } = await supabase
            .schema('workspace')
            .from('page_favorites')
            .upsert(
              { user_id: userId, page_id: pageId },
              { onConflict: 'user_id,page_id', ignoreDuplicates: true }
            );
          if (error) { await fetchFavorites(); return false; }
        }
      } catch {
        await fetchFavorites();
        return false;
      }
      return true;
    },
    [userId, favoriteIds, fetchFavorites]
  );

  return { favoriteIds, isFavorite, toggleFavorite, loading, refetch: fetchFavorites };
}
